import { Text } from "@/components/podkit/typography/Text";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { SubjectDisplay } from "@/components/SubjectDisplay";
import { WebhookProvider, WebhookType } from "gitpod-next-api/gitpod/v1/webhook_pb";
import { useState, type FC } from "react";
import type { PlainWebhook } from "@/queries/webhook-queries";
import { Link } from "react-router-dom";
import { webhookDetailsUrl } from "@/utils/automations";
import { pluralize } from "@/utils/strings";
import { WEBHOOK_PROVIDER_CONFIG, WEBHOOK_TYPE_CONFIG } from "@/components/webhooks/webhook-config";
import { WebhookActionsDropdown } from "@/components/webhooks/WebhookActionsDropdown";

type WebhookCardProps = PropsWithClassName<{
    webhook: PlainWebhook;
}>;

export const WebhookCard: FC<WebhookCardProps> = ({ webhook, className }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const name = webhook.metadata?.name || "Unnamed webhook";
    const description = webhook.metadata?.description;
    const creator = webhook.metadata?.creator;
    const provider = webhook.spec?.provider ?? WebhookProvider.UNSPECIFIED;
    const type = webhook.spec?.type ?? WebhookType.UNSPECIFIED;
    const scope = webhook.spec?.scope || "";
    const boundWorkflowCount = webhook.boundWorkflowCount;
    const ProviderIcon = WEBHOOK_PROVIDER_CONFIG[provider].icon;

    return (
        <Link
            to={webhookDetailsUrl(webhook.id)}
            state={{ canGoBack: true }}
            className={cn(
                "flex flex-col gap-3 rounded-xl border border-border-base p-4",
                "focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                { "shadow-sm": isDropdownOpen },
                { "hover:shadow-sm": !isDropdownOpen },
                className,
            )}
            data-testid="webhook-card"
            data-tracking-id="webhook-list-item"
        >
            <div>
                <div className="flex items-center justify-between gap-2">
                    <Text className="text-base font-medium text-content-primary">{name}</Text>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                            <ProviderIcon size="sm" className="text-content-secondary" />
                            <Text className="text-sm text-content-secondary">
                                {WEBHOOK_PROVIDER_CONFIG[provider].name}
                            </Text>
                        </div>
                        <WebhookActionsDropdown webhook={webhook} buttonSize="sm" onOpenChange={setIsDropdownOpen} />
                    </div>
                </div>
                {description && <Text className="text-base text-content-secondary">{description}</Text>}
            </div>
            <div className="flex items-center gap-3 text-sm text-content-secondary">
                <div className="flex items-center gap-1">
                    <Text className="text-content-muted">{WEBHOOK_TYPE_CONFIG[type].name}:</Text>
                    <Text className="text-content-primary">{scope}</Text>
                </div>
                <span className="text-content-muted">·</span>
                <Text>
                    {boundWorkflowCount} {pluralize(boundWorkflowCount, "automation")}
                </Text>
            </div>
            <div className="flex items-center gap-1">
                <SubjectDisplay subject={creator} />
            </div>
        </Link>
    );
};
